import { UpdateBotProfilePicture } from "@/components/Dialogs/UpdateBotProfilePicture";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import Icon from "@/icons";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function BotDisplay({ botId, user }: { botId: any, user: any }) {

    const getBot = useQuery({
        queryKey: ["bot", botId],
        retry: 1,
        retryDelay: 1000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const response = await fetch(`__SITE_BASE__/api/v1/bots/${botId}`);
            return response.json();
        }
    });

    const [botName, setBotName] = useState("");
    const [profilePicture, setProfilePicture] = useState("");
    const [isChanged, setIsChanged] = useState(false);
    const [botToken, setBotToken] = useState("");
    useEffect(() => {
        if (!getBot.isLoading && getBot.isError && !getBot.data?.bot && user) {
            window.location.href = "/developers";
        } else if (getBot.data && !botName && !isChanged) {
            setBotName(getBot?.data?.data?.user.username);
            setBotToken(getBot?.data?.data.bot.token);
            setProfilePicture(getBot?.data?.data.user.profilePicture);
        }
    }, [getBot]);

    useEffect(() => {
        if (profilePicture !== getBot.data?.data.user.profilePicture) {
            setIsChanged(true);
        } else {
            setIsChanged(false);
        }

        setProfilePicture(getBot.data?.data.user.profilePicture);
    }, [profilePicture]);

    useEffect(() => {
        if (botName !== getBot.data?.data.user.username) {
            setIsChanged(true);
        } else {
            setIsChanged(false);
        }
    }, [botName]);

    const [nameError, setNameError] = useState("");

    const updateBot = async () => {
        if (nameError) return;
        const response = await fetch(`__SITE_BASE__/api/v1/bots/${botId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                name: botName,
                profilePicture,
            }),
        });

        if (response.ok) {
            setIsChanged(false);
        } else {
            const data = await response.json();
            setNameError(data.message);
        }
    }

    const regenToken = async () => {
        const response = await fetch(`__SITE_BASE__/api/v1/bots/${botId}/token`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (response.ok) {
            const data = await response.json();
            setBotToken(data.token);
        }
    }

    const isActive = (path: string) => window.location.pathname === path;
    return (
        <div className="flex flex-col min-h-screen bg-brand-back">
            {getBot.isLoading ? (
                <div className="flex items-center justify-center h-full">
                    <Spinner />
                </div>
            ) : getBot.isError ? (
                <div className="text-white text-center mt-4">{getBot.error as unknown as string}</div>
            ) : (
                <div className="flex flex-col sm:flex-row h-full">
                    <div className="hidden sm:flex flex-col h-screen bg-brand-dark w-96 border-r border-brand-back">
                        <div className="flex flex-col gap-2 px-4 py-6">
                            <Link to="/developers" className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-brand-darkest cursor-pointer">
                                <h1 className="text-gray-300 font-semibold text-lg hover:text-white">Back</h1>
                            </Link>
                            <Link to={`/developers/bots/${botId}`} className={`flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-brand-darkest cursor-pointer ${isActive('/developers/bots/' + botId) ? 'bg-brand-darkest' : ''}`}>
                                <h1 className="text-gray-300 font-semibold text-lg hover:text-white">Overview</h1>
                            </Link>
                        </div>
                    </div>
                    <div className="flex flex-col flex-grow p-8">
                        <h1 className="text-white text-3xl font-bold mb-4">Bot</h1>
                        <div className="flex flex-col sm:flex-row items-start gap-4">
                            <div className="flex flex-col">
                                <h1 className="text-white text-xl ">Avatar</h1>
                                <div className="bg-brand-default p-4 rounded-lg">
                                    <div className="border-2 border-brand-green rounded-lg">
                                        <div className="relative group">
                                            {profilePicture ? (
                                                <img src={profilePicture} alt="Bot Icon" className="min-h-32 min-w-32 max-w-32 rounded-lg" />
                                            ) : botName && (
                                                <div className="min-h-32 min-w-32 max-w-32 rounded-lg bg-brand-darkest flex items-center justify-center text-white text-4xl font-semibold">
                                                    {botName[0].toUpperCase() || "B"}
                                                </div>
                                            )}
                                            <UpdateBotProfilePicture botId={botId} botName={botName} setProfilePicture={setProfilePicture} profilePicture={profilePicture}>
                                                <div className="group-hover:flex top-0 hidden items-center justify-center cursor-pointer bg-black bg-opacity-50 h-32 w-32 rounded-lg absolute">
                                                    <Icon name="picture" size="32" color="white" />
                                                </div>
                                            </UpdateBotProfilePicture>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col w-full gap-4">
                                <div className="flex flex-col gap-1">
                                    <h1 className="text-white font-bold text-sm mb-2">Username</h1>
                                    <Input
                                        className="rounded-t-lg sm:w-1/2 bg-brand-default border-r-0 border-l-0 border-t-0 border-b-brand-green placeholder:text-gray-300"
                                        placeholder="Bot Name"
                                        type="text"
                                        autoComplete="false"
                                        maxLength={12}
                                        value={botName}
                                        onChange={(e) => setBotName(e.target.value)}
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <h1 className="text-white font-bold text-sm mb-2">Token</h1>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            readOnly
                                            className="rounded-t-lg sm:w-1/2 bg-brand-default border-r-0 border-l-0 border-t-0 border-b-brand-green placeholder:text-gray-300"
                                            placeholder="Bot Token"
                                            type="text"
                                            autoComplete="false"
                                            value={botToken}
                                        />

                                        <button
                                            className="bg-brand-green p-2 rounded-lg text-white font-semibold hover:bg-green-500"
                                            onClick={regenToken}
                                        >
                                            Regenerate
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4">
                            <a
                                href={`/b/${getBot.data.data.user.id}`}
                                target="_blank"
                                className="bg-brand-green p-2 rounded-lg text-white font-semibold hover:bg-green-500"
                            >
                                Invite Bot
                            </a>
                        </div>
                        {isChanged && (
                            <div className="fixed bottom-16 sm:right-72 px-2 items-center justify-center mt-4 scale-90 sm:scale-100 sm:w-1/2 transition-all duration-300 ease-linear">
                                <div className="bg-brand-darkest flex gap-2 w-full items-center p-2 rounded-lg justify-between">
                                    <h1 className="text-white font-semibold text-lg">You have unsaved changes</h1>
                                    <button className="bg-brand-green px-5 py-2 rounded-lg text-white font-semibold hover:bg-green-500" onClick={updateBot}>Save Changes</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
